/**
 * SQL fragments shared by the transactions view and the rate lookup helpers.
 *
 * Exchange rates are stored one row per (source, target, date). A lookup for
 * `from -> to` accepts either a direct row (rate) or the inverse row
 * (1 / rate); direct rows win on ties.
 *
 * Dates are the yyyymmdd integers Actual uses everywhere. "Closest in time"
 * is measured in real days via julianday(), not by integer difference, so a
 * rate dated 2026-02-01 is closer to 2026-01-31 than one dated 2026-01-01.
 */

/** julianday() of a yyyymmdd integer SQL expression. */
export function julianDayOfInt(dateExpr: string): string {
  const s = `printf('%08d', ${dateExpr})`;
  return `julianday(substr(${s}, 1, 4) || '-' || substr(${s}, 5, 2) || '-' || substr(${s}, 7, 2))`;
}

type RateExprs = {
  /** SQL expression for the currency to convert from */
  from: string;
  /** SQL expression for the currency to convert to */
  to: string;
};

/**
 * The outer expressions are pulled into a one-row derived table `x` so the
 * ORDER BY only references `x.*`. The browser's SQLite (3.35, sql.js) rejects
 * outer-query references inside a subquery's ORDER BY ("no such column"),
 * while it does allow them in a correlated FROM-clause subquery.
 */
function rateSelect(
  { from, to }: RateExprs,
  dateExpr: string | null,
  orderBy: string,
): string {
  const dateCol = dateExpr == null ? '' : `, ${dateExpr} AS d`;
  return `(SELECT CASE WHEN r.source_currency = x.cur THEN r.rate ELSE 1.0 / r.rate END
             FROM exchange_rates r,
                  (SELECT ${from} AS cur, ${to} AS tgt${dateCol}) x
            WHERE r.tombstone = 0
              AND ((r.source_currency = x.cur AND r.target_currency = x.tgt)
                OR (r.source_currency = x.tgt AND r.target_currency = x.cur))
            ORDER BY ${orderBy},
                     CASE WHEN r.source_currency = x.cur THEN 0 ELSE 1 END ASC,
                     r.date DESC
            LIMIT 1)`;
}

/** Rate whose date is closest to `dateExpr` (a yyyymmdd integer expression). */
export function closestRateSubquery(
  exprs: RateExprs,
  dateExpr: string,
): string {
  return rateSelect(
    exprs,
    dateExpr,
    `ABS(${julianDayOfInt('r.date')} - ${julianDayOfInt('x.d')}) ASC`,
  );
}

/** Most recently dated rate, regardless of any transaction date. */
export function latestRateSubquery(exprs: RateExprs): string {
  return rateSelect(exprs, null, `r.date DESC`);
}

/**
 * True when a transaction's amount is already in the budget currency, i.e.
 * the account has no currency of its own, no budget currency is configured,
 * or the two match. `acct` / `budget` are SQL expressions for those codes.
 */
export function isBudgetCurrencyExpr(acct: string, budget: string): string {
  return `(${acct} IS NULL OR ${acct} = '' OR ${budget} IS NULL OR ${budget} = '' OR ${acct} = ${budget})`;
}
